import { useMemo } from 'react';
import StringUtils from '../utils/stringUtils';

import { TRIP_STATUS } from '../redux/api/trips/trips.model';
import { usePatchTripMutation } from '../redux/api/trips/tripApi';
import { useAppSelector } from '../redux/hooks';
import useAuth from '../providers/Auth/Auth.provider';

interface SegmentStatusOption {
  status: TRIP_STATUS;
  label: string;
  setStatus: (
    segmentId: string,
    prevStatus: TRIP_STATUS,
    includeArchived: boolean,
    tagsToInvalidate: any[]
  ) => Promise<number>;
}

interface UseSegmentStatus {
  statusOptions: SegmentStatusOption[];
  updateStatus: (
    segmentId: string,
    status: TRIP_STATUS,
    prevStatus: TRIP_STATUS,
    includeArchived: boolean,
    tagsToInvalidate: any[]
  ) => Promise<number>;
}

/**
 * This hook handles any status related actions for segment (progression and single updates)
 */
const useSegmentStatus = (): UseSegmentStatus => {
  const filters = useAppSelector((state) => state.activeTrips.filters);
  const {
    data: { userData }
  } = useAuth();
  const enableLtl = userData?.tenant?.ltl_enabled || false;
  const [patchTrip] = usePatchTripMutation();

  const statusOptions = useMemo<SegmentStatusOption[]>(() => {
    const statusValues = Object.values(TRIP_STATUS);

    // For LTL, we don't want to show the "Cancelled" status
    if (enableLtl) {
      statusValues.splice(statusValues.indexOf(TRIP_STATUS.CANCELLED), 1);
    }

    return statusValues.map(
      (status): SegmentStatusOption => ({
        status: status as TRIP_STATUS,
        label: StringUtils.getSegmentStatusLabel(status as TRIP_STATUS),
        setStatus: (
          segmentId: string,
          prevStatus: TRIP_STATUS,
          includeArchived: boolean,
          tagsToInvalidate: any[]
        ): Promise<number> =>
          updateStatus(
            segmentId,
            status as TRIP_STATUS,
            prevStatus,
            includeArchived,
            tagsToInvalidate
          )
      })
    );
  }, []);

  /**
   * @param segmentId
   * @param status
   * @returns response trip search response
   */
  const updateStatus = async (
    segmentId: string,
    status: TRIP_STATUS,
    prevStatus: TRIP_STATUS,
    includeArchived: boolean,
    tagsToInvalidate: any[]
  ): Promise<any> => {
    return await patchTrip({
      id: segmentId,
      body: { status },
      tagsToInvalidate,
      searchTripParams: { filters: { ...filters, includeArchived } },
      prevStatus
    }).unwrap();
  };

  return { statusOptions, updateStatus };
};

export default useSegmentStatus;
